/**
 * Flying Letter - Web Implementation (CSS Transforms + RAF)
 *
 * Implements true letter throwing animation on web using:
 * - CSS transforms (translateX, translateY, scale) for GPU acceleration
 * - requestAnimationFrame for smooth 60 FPS orchestration
 * - 4 chromatic layers (R/G/B/main) matching native implementation
 *
 * Multi-model validated (Gemini Pro 2.5, GPT-5 Pro, O3):
 * - 100% consensus on CSS + RAF approach
 * - will-change: transform for compositor optimization
 * - pointerEvents: none for performance
 *
 * @see FlyingLetter.native.tsx for Reanimated 3 native implementation
 */

import React, { useEffect, useRef, memo } from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import type { FlyingLetterProps, SpeedSettings } from './types';
import { CHROMATIC_COLORS } from './types';

/** Calculate flight duration based on speed settings */
function getFlightDuration(settings: SpeedSettings): number {
  const isExpress = settings.multiplier >= 3.0;
  const baseFlight = isExpress ? 300 : 450;
  return Math.max(baseFlight / settings.multiplier, settings.minFlight);
}

/** Easing function: cubic-bezier(0.15, 0.8, 0.25, 1) approximation */
function easeOutExpo(t: number): number {
  return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
}

/**
 * FlyingLetter web implementation using CSS transforms + RAF
 */
function FlyingLetterWeb({
  char,
  index,
  speedSettings,
  onLand,
  targetPosition = { x: 0, y: 0 },
}: FlyingLetterProps) {
  const containerRef = useRef<View>(null);
  const rafRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const hasLandedRef = useRef(false);

  // Initial random offsets for variety
  const initialOffsetX = useRef((Math.random() - 0.5) * 60);
  const initialOffsetY = useRef((Math.random() - 0.5) * 40);

  const flightDuration = getFlightDuration(speedSettings);

  useEffect(() => {
    // Skip animation if not on web or reduced motion preferred
    if (Platform.OS !== 'web') {
      onLand();
      return;
    }

    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      onLand();
      return;
    }

    startTimeRef.current = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTimeRef.current;
      const progress = Math.min(elapsed / flightDuration, 1);
      const eased = easeOutExpo(progress);

      if (containerRef.current) {
        // Get the DOM node for direct style manipulation (performance)
        // @ts-expect-error - accessing internal DOM node on web
        const domNode = containerRef.current._nativeTag || containerRef.current;
        if (domNode && domNode.style) {
          // Calculate current values
          const currentX = initialOffsetX.current * (1 - eased) + targetPosition.x * eased;
          const currentY = initialOffsetY.current * (1 - eased) + targetPosition.y * eased;
          const currentScale = 60 * (1 - eased) + 1 * eased; // 60 -> 1
          const currentOpacity = Math.min(progress / 0.6, 1); // Fade in during first 60%

          // Chromatic offset (converges to 0)
          const chromaOffset = speedSettings.chromaOffset * (1 - eased);

          // Apply transforms via CSS custom properties for layers
          domNode.style.transform = `translate(${currentX}px, ${currentY}px) scale(${currentScale})`;
          domNode.style.opacity = String(currentOpacity);
          domNode.style.setProperty('--chroma-offset', `${chromaOffset}px`);
        }
      }

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        // Animation complete - trigger landing
        if (!hasLandedRef.current) {
          hasLandedRef.current = true;
          // Small delay for landing bounce visual
          setTimeout(() => {
            onLand();
          }, 50);
        }
      }
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [flightDuration, onLand, speedSettings.chromaOffset, targetPosition.x, targetPosition.y]);

  // For non-web, just render static (this file is .web.tsx so should only run on web)
  if (Platform.OS !== 'web') {
    return (
      <View style={styles.container}>
        <Text style={styles.layerMain}>{char}</Text>
      </View>
    );
  }

  return (
    <View
      ref={containerRef}
      style={styles.container}
      pointerEvents="none"
    >
      {/* R layer - Pink */}
      <Text
        style={[
          styles.layer,
          styles.layerR,
          // @ts-expect-error - CSS custom property
          { transform: [{ translateX: 'var(--chroma-offset, 0)' }] },
        ]}
      >
        {char}
      </Text>

      {/* G layer - Lavender */}
      <Text style={[styles.layer, styles.layerG]}>
        {char}
      </Text>

      {/* B layer - Mint */}
      <Text
        style={[
          styles.layer,
          styles.layerB,
          // @ts-expect-error - CSS custom property (negative)
          { transform: [{ translateX: 'calc(var(--chroma-offset, 0) * -1)' }] },
        ]}
      >
        {char}
      </Text>

      {/* Main layer - White with glow */}
      <Text style={[styles.layer, styles.layerMain]}>
        {char}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
    // @ts-ignore -- web-only CSS property not in all RN ViewStyle definitions
    willChange: 'transform, opacity',
  },
  layer: {
    position: 'absolute',
    fontSize: 24,
    fontWeight: '800',
  },
  layerR: {
    color: CHROMATIC_COLORS.r,
    opacity: 0.5,
  },
  layerG: {
    color: CHROMATIC_COLORS.g,
    opacity: 0.5,
  },
  layerB: {
    color: CHROMATIC_COLORS.b,
    opacity: 0.5,
  },
  layerMain: {
    color: CHROMATIC_COLORS.main,
    // Web: Use CSS textShadow instead of deprecated RN textShadow* properties
    textShadow: '0px 0px 15px rgba(212, 165, 255, 0.4)',
  } as any,
});

export default memo(FlyingLetterWeb);
